/**
 * Whether a batch is past its expiry date.
 *
 * Compared as YYYY-MM-DD strings, which sort correctly and avoid parsing a bare date into a
 * local Date — the round trip that reports the day before east of Greenwich. A batch expiring
 * today is still in date.
 */
export function isBatchExpired(row, today) {
    return !!row.expiryDate && row.expiryDate < today;
}
/**
 * How a batch's quantity divides across QC states.
 *
 * SHARED BY BOTH SIDES, which is why it lives here. The site's batch register, the dispatch
 * queue and the brand's production tables all report QC quantities, and a second implementation
 * would be two screens disagreeing about how much stock is cleared — the exact class of defect
 * this codebase keeps having to hunt down.
 *
 * NONE OF THIS IS STORED, and it cannot be, because the batch records a JUDGEMENT rather than
 * four running totals: `qcPassedQuantity` / `qcFailedQuantity` are written only when a verdict
 * covered part of a batch, and are null — the ordinary case — when it covered all of it. So a
 * whole-batch verdict has to be read off the status against the quantity made.
 *
 * EXPIRY OVERRIDES A PASS. A batch that passed QC and then went out of date is not approved
 * stock in any useful sense — it cannot be consumed or dispatched — so its quantity is reported
 * as expired instead. That keeps "QC approved" meaning what a supply-chain reader expects: what
 * is cleared and still usable.
 *
 * Pending is NOT moved when a batch expires. Nobody has ruled on it, and quietly reclassifying
 * it would hide a batch that still needs a decision.
 */
export function batchQcSplit(row, today) {
    const made = Number(row.made) || 0;
    // Both or neither, always — a partial verdict records what it covered on both sides.
    const isSplit = row.qcPassedQuantity !== null && row.qcFailedQuantity !== null;
    // Written as a branch rather than nested ternaries: this package lints at zero warnings and
    // forbids them, and the three cases read better spelled out than folded together anyway.
    let passed = 0;
    let rejected = 0;
    if (isSplit) {
        // A batch whole-failed AFTER a split verdict keeps its rejected total (settled,
        // ledger-backed) but nothing in it is approved any more — FAILED blocks the lot.
        passed = row.qcStatus === 'FAILED' ? 0 : Number(row.qcPassedQuantity);
        rejected = Number(row.qcFailedQuantity);
    }
    else if (row.qcStatus === 'PASSED') {
        passed = made;
    }
    else if (row.qcStatus === 'FAILED') {
        rejected = made;
    }
    /**
     * PENDING reopens only what still exists to rule on.
     *
     * A batch with no verdict at all is entirely pending. A batch sent BACK to pending after a
     * split keeps its rejected total — those units left through real write-offs and a status
     * flip cannot resurrect them — so what awaits the new verdict is what remains: made minus
     * the settled rejects (and minus anything oddly still standing passed). A ruled batch has
     * nothing pending, split or not.
     */
    const isRuled = row.qcStatus === 'PASSED' || row.qcStatus === 'FAILED';
    const pending = isRuled ? 0 : Math.max(0, made - passed - rejected);
    const isExpired = isBatchExpired(row, today);
    return isExpired
        ? { approved: 0, pending, rejected, expired: passed }
        : { approved: passed, pending, rejected, expired: 0 };
}
export function qcDisplayStatus(row) {
    if (!row.qcStatus)
        return null;
    if (row.qcStatus === 'PASSED' && Number(row.qcFailedQuantity ?? 0) > 0) {
        // Nothing left standing passed is not "partially" anything: every unit was rejected
        // and written off. Distinct from FAILED, which quarantines stock that still exists.
        return Number(row.qcPassedQuantity ?? 0) > 0
            ? 'PARTIALLY_PASSED'
            : 'REJECTED';
    }
    return row.qcStatus;
}
//# sourceMappingURL=batchQcSplit.js.map